import { useState, useRef, useEffect } from "react";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { v4 as uuidv4 } from "uuid";

export default function ArchitectStampGenerator() {
  const [name, setName] = useState("John Doe");
  const [license, setLicense] = useState("C-00000");
  const [renewalDate, setRenewalDate] = useState("");
  const [qrUrl, setQrUrl] = useState(null);
  const [showBackground, setShowBackground] = useState(true);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState(100);
  const [authenticated, setAuthenticated] = useState(false);
  const [serialNumber, setSerialNumber] = useState(null);
  const stampRef = useRef(null);

  const containerWidth = 400;
  const averageCharWidth = 22;
  const estimatedFontSize = Math.min(48, Math.floor(containerWidth / (name.length * (averageCharWidth / 24))));

  useEffect(() => {
    const generateQR = async () => {
      const qrData = `${name} | ${license} | ${renewalDate}`;
      const qrCanvas = document.createElement("canvas");
      await QRCode.toCanvas(qrCanvas, qrData, { margin: 0, scale: 6 });
      setQrUrl(qrCanvas.toDataURL());
    };
    generateQR();
  }, [name, license, renewalDate]);

  const handleUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      alert("Only JPG and PNG files are supported.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => setUploadedImage(reader.result);
    reader.readAsDataURL(file);
  };

  const downloadImage = async (format) => {
    if (!stampRef.current) return;

    await new Promise((resolve) => setTimeout(resolve, 100));

    const canvas = await html2canvas(stampRef.current, {
      scale: 3,
      useCORS: true,
      backgroundColor: null,
    });

    const dataUrl = canvas.toDataURL("image/png");

    if (format === "jpg" || format === "png") {
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `architect-stamp.${format}`;
      link.click();
    } else if (format === "pdf") {
      const pdf = new jsPDF();
      const imgProps = pdf.getImageProperties(dataUrl);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(dataUrl, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save("architect-stamp.pdf");
    } else if (format === "dwg" || format === "dxf") {
      alert(`${format.toUpperCase()} export not supported in browser.`);
    }
  };

  const handleAuthenticate = () => {
    const serial = uuidv4();
    setSerialNumber(serial);
    setAuthenticated(true);

    const csvContent = `Serial Number,Name,License,Renewal Date\n"${serial}","${name}","${license}","${renewalDate}"`; // CSV format
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.setAttribute("href", URL.createObjectURL(blob));
    link.setAttribute("download", `stamp-authentication-${serial}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">California Architect Stamp Generator</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div>
          <Label htmlFor="name">Name</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="license">License Number</Label>
          <Input id="license" value={license} onChange={(e) => setLicense(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="renewal">Renewal Date</Label>
          <Input id="renewal" type="date" value={renewalDate} onChange={(e) => setRenewalDate(e.target.value)} />
        </div>
        <div className="col-span-1 sm:col-span-3">
          <Label htmlFor="upload">Upload Signature (for best results, use an image with a transparent background)</Label>
          <Input id="upload" type="file" accept="image/*" onChange={handleUpload} />
        </div>
        {uploadedImage && (
          <div className="col-span-1 sm:col-span-3 flex flex-wrap justify-center gap-2 text-black">
            <Button variant="outline" onClick={() => setImagePosition(pos => ({ ...pos, y: pos.y - 5 }))}>⬆️</Button>
            <Button variant="outline" onClick={() => setImagePosition(pos => ({ ...pos, y: pos.y + 5 }))}>⬇️</Button>
            <Button variant="outline" onClick={() => setImagePosition(pos => ({ ...pos, x: pos.x - 5 }))}>⬅️</Button>
            <Button variant="outline" onClick={() => setImagePosition(pos => ({ ...pos, x: pos.x + 5 }))}>➡️</Button>
            <Button variant="outline" onClick={() => setImageSize(size => Math.max(10, size - 5))}>➖</Button>
            <Button variant="outline" onClick={() => setImageSize(size => size + 5)}>➕</Button>
          </div>
        )}
      </div>

      <Card className="p-4">
        <CardContent>
          <div ref={stampRef} className="relative w-[512px] h-[512px] mx-auto overflow-visible">
            {showBackground && (
              <img
                src="/src/California_Architect_1024x1024@2xSC.jpg"
                alt="Architect Stamp"
                className="w-full h-full object-contain"
              />
            )}

            <div
              className="absolute top-[25%] left-1/2 transform -translate-x-1/2 font-bold text-center whitespace-nowrap"
              style={{ fontSize: `${estimatedFontSize}px`, maxWidth: "90%" }}
            >
              {name.toUpperCase()}
            </div>

            <div className="absolute top-[45%] left-1/2 transform -translate-x-1/2 text-4xl font-semibold">
              {license}
            </div>

            {uploadedImage && (
              <div
                className="absolute"
                style={{
                  top: `calc(60% + ${imagePosition.y}px)`,
                  left: `calc(50% + ${imagePosition.x}px)`,
                  transform: "translate(-50%, -50%)",
                }}
              >
                <img
                  src={uploadedImage}
                  alt="Uploaded"
                  style={{ width: `${imageSize}px`, height: "auto", maxHeight: "80px" }}
                  className="object-contain"
                />
              </div>
            )}

            <div className="absolute bottom-[25%] left-1/2 transform -translate-x-1/2 text-2xl font-semibold">
              {renewalDate && new Date(renewalDate).toLocaleDateString()}
            </div>

            {qrUrl && (
              <div
                className="absolute top-[14%] left-1/2 transform -translate-x-1/2 w-[60px] h-[60px]"
                style={{ overflow: "visible", zIndex: 10 }}
              >
                <img
                  src={qrUrl}
                  alt="QR Code"
                  className="w-full h-full object-contain"
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="mb-4 flex items-center gap-2">
        <input
          type="checkbox"
          id="transparent"
          checked={!showBackground}
          onChange={() => setShowBackground((prev) => !prev)}
        />
        <label htmlFor="transparent" className="text-sm">
          Transparent Export (hide background)
        </label>
      </div>

      <div className="flex gap-2 justify-center mt-4">
        <Button onClick={() => downloadImage("png")}>Save as PNG</Button>
        <Button onClick={() => downloadImage("jpg")}>Save as JPG</Button>
        <Button onClick={() => downloadImage("pdf")}>Save as PDF</Button>
        <Button onClick={() => downloadImage("dwg")}>Export as DWG</Button>
        <Button onClick={() => downloadImage("dxf")}>Export as DXF</Button>
      </div>

      <div className="flex justify-center mt-6">
        <Button onClick={handleAuthenticate}>Authenticate Stamp</Button>
      </div>

      {authenticated && serialNumber && (
        <div className="mt-4 text-center text-sm text-gray-600">
          Your stamp has been authenticated. Access it at: <br />
          <a
            href={`/user/${serialNumber}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 underline"
          >
            /user/{serialNumber}
          </a>
        </div>
      )}
    </div>
  );
}
